import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { motion } from 'framer-motion';
import { products } from '../products';

const gold = '#DBAE6E';

function ProductSlider({ onProductSelect, selectedProductId }) {
  const navigate = useNavigate();
  const { id } = useParams();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(3);

  // Responsive items per page
  useEffect(() => {
    const updateItemsPerPage = () => {
      setItemsPerPage(window.innerWidth < 768 ? 2 : 3);
    };

    updateItemsPerPage();
    window.addEventListener('resize', updateItemsPerPage);
    return () => window.removeEventListener('resize', updateItemsPerPage);
  }, []);

  // Navigation logic
  const nextSlide = () => {
    setCurrentIndex((prevIndex) =>
      prevIndex + itemsPerPage >= products.length ? 0 : prevIndex + itemsPerPage
    );
  };

  const prevSlide = () => {
    setCurrentIndex((prevIndex) =>
      prevIndex <= 0 ? Math.max(0, products.length - itemsPerPage) : prevIndex - itemsPerPage
    );
  };

  // Slide transition offset
  const slideOffset = window.innerWidth < 768 ? -currentIndex * 168 : -currentIndex * 212; // 160px + 8px gap, 200px + 12px gap

  // Card dimensions
  const cardWidth = { base: '160px', md: '200px' };
  const cardHeight = { base: '220px', md: '260px' };

  // Handle card click
  const handleCardClick = (product) => {
    navigate(`/product/${product.id}`);
    if (onProductSelect) {
      onProductSelect(product);
    }
  };

  // Title animation
  const titleVariants = {
    hidden: { opacity: 0, y: -15 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: 'easeOut' } },
  };

  return (
    <div className="relative max-w-7xl py-0 px-0">
      {/* Section Title */}
      <motion.h2
        className="text-xl md:text-2xl font-bold mb-3 text-center tracking-tight"
        style={{ color: gold }}
        variants={titleVariants}
        initial="hidden"
        animate="visible"
      >
        More by Laynered
      </motion.h2>

      <div className="relative overflow-hidden">
        {/* Navigation Buttons */}
        <motion.button
          className={`absolute left-[-1px] md:left-[-12px] top-1/2 transform -translate-y-1/2 w-10 h-7 md:w-8 h-8 rounded-full
            shadow-sm flex items-center justify-center transition-all duration-300 z-10
            ${currentIndex === 0 ? 'opacity-30 cursor-not-allowed' : 'hover:shadow-gold/70'}`}
          onClick={prevSlide}
          disabled={currentIndex === 0}
          whileHover={{ scale: currentIndex === 0 ? 1 : 1.15 }}
          whileTap={{ scale: 0.85 }}
          style={{ backgroundColor: gold, color: 'black' }}
          aria-label="Previous products"
        >
          <ChevronLeft size={18} />
        </motion.button>
        <motion.button
          className={`absolute right-[-1px] md:right-[-12px] top-1/2 transform -translate-y-1/2 w-10 h-7 md:w-8 h-8 rounded-full
            shadow-sm flex items-center justify-center transition-all duration-300 z-10
            ${currentIndex >= products.length - itemsPerPage ? 'opacity-30 cursor-not-allowed' : 'hover:shadow-gold/70'}`}
          onClick={nextSlide}
          disabled={currentIndex >= products.length - itemsPerPage}
          whileHover={{ scale: currentIndex >= products.length - itemsPerPage ? 1 : 1.15 }}
          whileTap={{ scale: 0.85 }}
          style={{ backgroundColor: gold, color: 'black' }}
          aria-label="Next products"
        >
          <ChevronRight size={18} />
        </motion.button>

        {/* Product Cards */}
        <motion.div
          className="flex gap-2 md:gap-3"
          style={{ width: 'fit-content' }}
          animate={{ x: slideOffset }}
          transition={{ type: 'spring', stiffness: 200, damping: 20 }}
        >
          {products.map((product) => {
            const defaultColor = product.colors[0];
            const defaultImage = product.images[defaultColor][0];
            const discountedPrice = product.price * (1 - product.discount);
            const isSelected = product.id === parseInt(id) || product.id === selectedProductId;

            return (
              <div
                key={product.id}
                className={`flex-shrink-0 flex flex-col items-center group cursor-pointer
                  rounded-lg transition-all duration-300 border
                  ${isSelected
                    ? 'scale-102 border-[var(--gold)] shadow-[var(--gold)]/50'
                    : 'hover:scale-103 hover:shadow-[var(--gold)]/40'}
                  bg-white text-black dark:bg-black dark:text-white`}
                style={{
                  width: window.innerWidth < 768 ? cardWidth.base : cardWidth.md,
                  height: window.innerWidth < 768 ? cardHeight.base : cardHeight.md,
                  borderColor: gold,
                }}
                onClick={() => handleCardClick(product)}
              >
                {/* Image Container */}
                <div
                  className="w-full h-[65%] relative overflow-hidden rounded-t-lg"
                  style={{ height: '65%' }}
                >
                  <motion.img
                    src={defaultImage}
                    alt={product.name}
                    className="w-full h-full object-cover transition-transform duration-400"
                    whileHover={{ scale: 1.08 }}
                  />
                  {product.discount > 0 && (
                    <div
                      className="absolute top-1 right-1 font-bold px-1.5 py-0.5 rounded-full text-[10px]"
                      style={{ backgroundColor: gold, color: 'black' }}
                    >
                      -{(product.discount * 100)}%
                    </div>
                  )}
                </div>

                {/* Content Below */}
                <div className="w-full px-2 py-1 flex-1 flex flex-col justify-end text-center">
                  <h3
                    className="text-xs md:text-sm font-medium truncate max-w-full group-hover:text-gold/80 transition-colors duration-300"
                    style={{ color: gold }}
                  >
                    {product.name}
                  </h3>
                  <div className="flex items-center justify-center gap-1 mt-0.5">
                    <span
                      className="text-sm md:text-base font-semibold"
                      style={{ color: gold }}
                    >
                      ₹{discountedPrice.toFixed(0)}
                    </span>
                    {product.discount > 0 && (
                      <span
                        className="text-[10px] md:text-xs line-through"
                        style={{ color: gold + '80' }}
                      >
                        ₹{product.price.toFixed(0)}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </motion.div>
      </div>

      {/* Progress Dots */}
      {products.length > itemsPerPage && (
        <div className="flex justify-center gap-1 mt-2">
          {Array(Math.ceil(products.length / itemsPerPage))
            .fill(0)
            .map((_, idx) => (
              <motion.div
                key={idx}
                className={`w-1 h-1 rounded-full transition-all duration-300 cursor-pointer
                  ${Math.floor(currentIndex / itemsPerPage) === idx ? 'bg-gold scale-125' : 'bg-gold/40'}`}
                whileHover={{ scale: 1.6 }}
                whileTap={{ scale: 1 }}
                onClick={() => setCurrentIndex(idx * itemsPerPage)}
                style={{ backgroundColor: Math.floor(currentIndex / itemsPerPage) === idx ? gold : undefined }}
                aria-label={`Go to slide ${idx + 1}`}
              />
            ))}
        </div>
      )}
    </div>
  );
}

export default ProductSlider;
